<template>
    <Card :title="$t('labels.products')">
        <template #header-elements>
            <Button size="sm" color="teal" class="mr-2" :disabled="disabled" @click="$refs.ImportFromFileModal.show()">
                {{ $t("buttons.connect_from_file") }}
            </Button>
            <Button size="sm"
                @click="$refs.createFromCategoryModal.show()"
                :disabled="disabled"
                color="teal"
                class="mr-2"
            >
                {{ $t('buttons.create_from_product_type') }}
            </Button>
            <Button size="sm" color="light" @click="showFilters = !showFilters" class="mr-2">
                <i class="icon-filter3 mr-1"></i>
                {{ `(${sumOfFilters})` }}
            </Button>
            <Dropdown
                :title="$t('buttons.actions')"
                icon="icon-menu7"
                :disabled="selectedIds.length === 0"
                class="mr-2"
            >
                <template #menu>
                    <a @click="destroy" class="dropdown-item">
                        <i class="icon-bin2"></i>
                        {{ $t('buttons.delete') }}
                    </a>
                    <a @click="recalculateSelectedIds" class="dropdown-item">
                        <i class="icon-calculator2 "></i>
                        {{ $t('buttons.recalculate') }}
                    </a>
                </template>
            </Dropdown>
        </template>
        <div class="card-body" v-if="showFilters">
            <div class="row">
                <div class="col-12 col-md-3 col-xl-2">
                    <Select
                        :model-value="filters.has_moves"
                        @update:model-value="(modelValue) => filters.has_moves = modelValue.key"
                        :options="filterOptions"
                        :selected-value="filterOptions.find(option => option.key === filters.has_moves)?.value"
                        :disabled="disabled"
                        option-key="key"
                        option-value="value"
                    />
                </div>
            </div>
        </div>
        <DataTable
            :url="table.url"
            :columns="table.columns"
            :filters-props="filters"
            ref="DataTable"
            :orders-props="[{ column: 0, dir: 'desc'}]"
            :check-box-props="true"
            @changeSelectedIds="newSelectedIds => selectedIds = newSelectedIds"
        >
            <template #rowForCreate>
                <td class="no-cursor-change"></td>
                <td class="no-cursor-change"></td>
                <td class="no-cursor-change"></td>
                <td class="no-cursor-change">
                    <Autocomplete
                        @update:model-value="newProduct => selectProductItem(newProduct.product)"
                        :model-value="productItem.product_item_id"
                        :selected-value="productItem.label"
                        option-key="product.uuid"
                        option-value="product.name"
                        url="/management/warehouses/products/availability"
                        ref="Autocomplete"
                        :disabled="disabled"
                        :query-params="{
                            extra: 1,
                            only_with_relation: 0,
                            orders: [
                                {column: 'automatic_arrival', dir: 'desc'}
                            ],
                            warehouse_uuids: [warehouse?.uuid]}"
                        :additional-information="true"
                        :selected-values="$refs.DataTable ? $refs.DataTable.meta.product_uuids : []"
                    >
                        <template #optionItem="{option}">
                            {{ option.product.name }}
                            <ProductAvailabilitySelectOption :option="option"/>
                        </template>
                    </Autocomplete>
                </td>
                <td class="no-cursor-change">
                    <Input
                        ref="ProductItemAmount"
                        @keydown.enter="focusNextInput()"
                        v-model="productItem.factual_amount"
                        :disabled="disabled"
                        type="number"
                        tabindex="2"
                    />
                </td>
                <td class="no-cursor-change"></td>
                <td class="no-cursor-change"></td>
                <td class="no-cursor-change" style="text-align:center;">
                    <Button size="sm"
                        tabindex="4"
                        :disabled="disabled"
                        @click="focusNextInput()"
                        @keydownEnter="focusNextInput()"
                        color="teal"
                    >
                        {{ $t('buttons.create') }}
                    </Button>
                </td>
            </template>
            <template v-slot:row-main_image_url="{ row }">
                <ImageZoomComponent
                    :url="row.main_image_url !== null ? row.main_image_url : PLACEHOLDER_IMAGE_URL"
                    :alt="row.title"
                    :default-width="50"
                    :default-height="50"
                />
            </template>
            <template v-slot:row-sku="{row}">
                <router-link v-if="row?.product_item_uuid" :to="{ name: 'EditProductPage', params: { productUuid: row.product_item_uuid } }" style="white-space: nowrap;">{{ truncateTitle(row.title) }}</router-link>
                <span v-else style="white-space: nowrap;">{{ truncateTitle(row.title) }}</span><br/>
                <span class="text-muted">{{ row.sku }}</span>
            </template>
            <template #row-factual_amount="{row}">
                <div style="display: flex; justify-content: space-between;">
                    <div>
                        <span class="font-weight-semibold">{{ Amount.factory(row.factual_amount).toMeasurementString(row.measurement.name) }}</span>
                        <small class="text-muted d-block cursor-pointer">
                            <span v-if="row.has_moves" class="text-warning underline" @click="$refs.MovesModal.show(row)">
                                {{ $t('alerts.move_detected') }}
                            </span>
                            <span v-else class="text-muted underline">
                                {{ $t('alerts.move_not_detected') }}
                            </span>
                        </small>
                    </div>
                    <Popover  @showPopover="showFields(row)" @hidePopover="hideFields" v-if="!disabled">
                        <template #body>
                            <label>{{ $t('table.titles.factual_amount') }}</label>
                            <Input
                                id="input"
                                type="number"
                                v-model="basic[0].value"
                            />
                            <Button size="sm"
                                id="button"
                                @click="edit(row.uuid)"
                                color="teal"
                            >
                                {{ $t('buttons.edit') }}
                            </Button>
                        </template>
                    </Popover>
                </div>
            </template>
            <template #row-actual_amount="{row}">
                <span>{{ Amount.factory(row.actual_amount).toMeasurementString(row.measurement.name) }}</span>
            </template>
            <template #row-diff_amount="{row}">
                <span>{{ Amount.factory(row.diff_amount).toMeasurementString(row.measurement.name) }}</span>
            </template>
            <template #row-created_at="{row}">
                {{ DateFormatter.formatDateTime(row.created_at) }}
            </template>
        </DataTable>
    </Card>
    <CreateFromCategoryModal
        @updateTable="$refs.DataTable.load()"
        :inventory-uuid="inventory.uuid"
        ref="createFromCategoryModal"
    />
    <MovesModal
        :inventory="inventory"
        :warehouse="warehouse"
        :exitDeclarationStatusUuids="exitDeclarationStatusUuids"
        :arrivalDeclarationStatusUuids="arrivalDeclarationStatusUuids"
        @updateTable="$refs.DataTable.load()"
        ref="MovesModal"
    />
    <ImportFromFileModal
        @uploaded="$refs.DataTable.load()"
        :inventory="inventory"
        ref="ImportFromFileModal"
    />
</template>

<script>
import Card from '../../../../../components/new/CardComponent.vue'
import DataTable from '../../../../../components/table/DataTableComponent.vue'
import Button from '../../../../../components/new/button/AppButton.vue'
import Input from '../../../../../components/new/form/Input.vue'
import Popover from '../../../../../components/PopoverComponent.vue'
import Autocomplete from '../../../../../components/new/form/autocomplete/AppAutoComplete.vue'
import Dropdown from '../../../../../components/DropdownComponent.vue'
import CreateFromCategoryModal from '../modals/CreateProductItemsFromCategoryModal.vue'
import MovesModal from '../modals/MovesModal.vue'
import Select from '../../../../../components/new/form/select/AppSelect.vue'
import ProductAvailabilitySelectOption from '../../../../../components/new/form/select/ProductAvailabilitySelectOption.vue'
import ImportFromFileModal from '../modals/ImportFromFileModal.vue'
import ImageZoomComponent from '../../../../../components/new/image/ImageZoomComponent.vue'
import Amount from '../../../../../services/Amount.ts'
import { httpClient } from '../../../../../services/HttpClient.ts'
import { PLACEHOLDER_IMAGE_URL } from '../../../../../constants.ts'
import DateFormatter from '../../../../../services/DateFormatter.ts'
import MessageNotificator from '../../../../../services/MessageNotificator'

export default {
    name: 'InventoryProductItemsTableCard',
    components: {
        Card,
        DataTable,
        Button,
        Input,
        Popover,
        Autocomplete,
        Dropdown,
        CreateFromCategoryModal,
        MovesModal,
        Select,
        ProductAvailabilitySelectOption,
        ImportFromFileModal,
        ImageZoomComponent
    },
    props: {
        inventory: {
            type: Object,
            required: true
        },
        warehouse: {
            type: Object,
            default: null
        },
        exitDeclarationStatusUuids: {
            type: Array,
            default: () => []
        },
        arrivalDeclarationStatusUuids: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            DateFormatter,
            PLACEHOLDER_IMAGE_URL,
            Amount: Amount,
            table: {
                url: `/management/inventories/${this.$route.params.inventoryUuid}/inventory-product-items/table`,
                columns: [
                    {
                        data: "id",
                        name: "id",
                        searchable: 1,
                        orderable: true,
                        field: this.$t('table.titles.id')
                    },
                    {
                        data: "main_image_url",
                        name: "main_image_url",
                        searchable: 0,
                        orderable: false,
                        field: this.$t('table.titles.image')
                    },
                    {
                        data: "sku",
                        name: "sku",
                        width: '454px',
                        searchable: 1,
                        orderable: true,
                        field: this.$t('table.titles.name')
                    },
                    {
                        data: "factual_amount",
                        name: "factual_amount",
                        searchable: 0,
                        orderable: true,
                        field: this.$t('table.titles.factual_amount')
                    },
                    {
                        data: "actual_amount",
                        name: "actual_amount",
                        searchable: 0,
                        orderable: true,
                        field: this.$t('table.titles.actual_amount')
                    },
                    {
                        data: "diff_amount",
                        name: "diff_amount",
                        searchable: 0,
                        orderable: true,
                        field: this.$t('table.titles.diff_amount')
                    },
                    {
                        data: "created_at",
                        name: "created_at",
                        searchable: 0,
                        orderable: true,
                        field: this.$t('table.titles.created_at')
                    },
                ]
            },
            basic: [
                {
                    name: 'factual_amount',
                    value: null
                }
            ],
            productItem: {
                product_item_id: null,
                label: '',
                factual_amount: null
            },
            selectedIds: [],
            showFilters: false,
            sumOfFilters: 0,
            filterOptions: [
                {
                    key: null,
                    value: this.$t('labels.without_filter')
                },
                {
                    key: 1,
                    value: this.$t('alerts.move_detected')
                },
                {
                    key: 0,
                    value: this.$t('alerts.move_not_detected')
                }
            ],
            filters: {
                has_moves: null
            }
        }
    },
    methods: {
        truncateTitle(title) {
            if (title.length <= 80) {
                return title
            }

            const truncated = title.slice(0, 80)

            return `${truncated}...`
        },
        destroy() {
            new MessageNotificator().confirm(`${this.$t('alerts.confirm_for_delete', {model: this.$t('labels.product_item')})}`, () => {
                httpClient.delete(`/management/inventories/${this.$route.params.inventoryUuid}/product-items`, {
                    product_item_ids: this.selectedIds
                }).then(() => {
                    this.selectedIds = []
                    this.$refs.DataTable.load()
                })
            })
        },
        showFields(row) {
            this.basic = [
                {
                    name: 'factual_amount',
                    value: row.factual_amount
                }
            ]
        },
        hideFields() {
            this.basic = [
                {
                    name: 'factual_amount',
                    value: null
                }
            ]
        },
        edit(productItemUuid) {
            httpClient.put(`/management/inventories/${this.$route.params.inventoryUuid}/product-items/${productItemUuid}`, {
                fields: this.basic
            }).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'))
                this.$refs.DataTable.load()
            })
        },
        selectProductItem(productItem) {
            new Promise((resolve) => {
                this.productItem.product_item_id = productItem.id;
                this.productItem.label = productItem.name;
                resolve()
            }).then(() => {
                this.$refs.ProductItemAmount.focus()
            })
        },
        focusNextInput() {
            let request = {}
                Object.entries(this.productItem).forEach(([key, value]) => {
                    if(key !== 'label' && value !== null && value !== "" ) {
                        request[key] = value
                    }
                })
            httpClient.post(`/management/inventories/${this.$route.params.inventoryUuid}/product-items`, request).then(() => {
                new MessageNotificator().success(this.$t('alerts.added', { model: this.$t('labels.product_item') }));
                this.$refs.DataTable.load();
                this.productItem.product_item_id = null;
                this.productItem.label = '';
                this.productItem.factual_amount = null;
            })
        },
        setSumOfFilters() {
            return Object.keys(this.filters).filter(key => this.filters[key] !== null).length;
        },
        recalculateSelectedIds() {
            httpClient.post(`/management/inventories/${this.$route.params.inventoryUuid}/product-items/add-moves`, {
                inventory_product_item_ids: this.selectedIds
            }).then(() => {
                new MessageNotificator().success(this.$t('alerts.success'))
                this.$refs.DataTable.load()
            })
        }
    },
    watch: {
        'filters': {
            handler(newValue) {
                this.$refs.DataTable.load(newValue)
                this.sumOfFilters = this.setSumOfFilters()
            },
            deep: true
        },
    },
    mounted() {
        this.sumOfFilters = this.setSumOfFilters();
    },
    computed: {
        disabled() {
            if (!this.inventory) {
                return true
            }

            return this.inventory.status.type !== 'process'
        }
    }
}
</script>
<style scoped>
#input {
    margin-bottom: 10px;
}
#button {
    float: right; margin-bottom: 10px;
}
.dropdown-menu {
    right: 0px;
    left:auto;
    top: 105%
}
.cursor-pointer {
    cursor: pointer;
}
.no-cursor-change {
    cursor: default;
}
.underline {
    text-decoration: underline;
}
</style>
